import React, { useRef, useState } from "react";
import { Upload, Download, CheckCircle, XCircle, Loader2, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

const TEMPLATE_URLS = {
  assignments: `${BACKEND}/api/templates/assignments`,
  consumption: `${BACKEND}/api/templates/consumption`,
};

function ImportSection({ title, description, endpoint, templateKey }) {
  const inputRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | uploading | success | error
  const [result, setResult] = useState(null);

  async function handleFile(file) {
    if (!file) return;
    setStatus("uploading");
    setResult(null);

    const form = new FormData();
    form.append("file", file);

    try {
      const res = await fetch(`${BACKEND}${endpoint}`, {
        method: "POST",
        body: form,
        credentials: "include",
      });
      const data = await res.json();
      setResult(data);
      setStatus(data.ok ? "success" : "error");
      if (data.ok) toast.success(`Imported ${data.inserted} records`);
    } catch (err) {
      setResult({ ok: false, errors: [{ row: 0, error: String(err) }] });
      setStatus("error");
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  const stateIcon = {
    idle: <Upload className="w-8 h-8 text-steel-light" />,
    uploading: <Loader2 className="w-8 h-8 text-orange animate-spin" />,
    success: <CheckCircle className="w-8 h-8 text-green-500" />,
    error: <XCircle className="w-8 h-8 text-red-500" />,
  }[status];

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div>
          <CardTitle className="text-base">{title}</CardTitle>
          <p className="text-sm text-steel mt-1">{description}</p>
        </div>
        <a
          href={`/templates/${templateKey}_template.csv`}
          download
          className="shrink-0 flex items-center gap-1.5 text-xs text-steel hover:text-graphite border border-border rounded-lg px-3 py-2"
          onClick={(e) => {
            e.preventDefault();
            // Serve from backend templates folder via a simple download
            const link = document.createElement("a");
            link.href = `${BACKEND}/api/templates/${templateKey}`;
            link.download = `${templateKey}_template.csv`;
            link.click();
          }}
        >
          <Download className="w-3.5 h-3.5" /> Template
        </a>
      </CardHeader>
      <CardContent className="space-y-4">
        <div
          className="border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer hover:bg-surface transition-colors"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          onClick={() => inputRef.current?.click()}
        >
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />
          <div className="flex flex-col items-center gap-2">
            {stateIcon}
            <p className="text-sm font-medium text-graphite">
              {status === "idle" && "Drop CSV here or click to browse"}
              {status === "uploading" && "Uploading and validating…"}
              {status === "success" && `Successfully imported ${result?.inserted} records`}
              {status === "error" && `Import failed — see errors below`}
            </p>
          </div>
        </div>

        {/* Error list */}
        {result && !result.ok && result.errors?.length > 0 && (
          <div className="rounded-xl bg-red-50 border border-red-200 divide-y divide-red-100 max-h-64 overflow-y-auto">
            {result.errors.map((e, i) => (
              <div key={i} className="flex items-start gap-3 px-4 py-2.5">
                <Badge variant="destructive" className="shrink-0 text-xs mt-0.5">Row {e.row}</Badge>
                <p className="text-sm text-red-700">{e.error}</p>
              </div>
            ))}
          </div>
        )}

        {result?.ok && (
          <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-xl px-4 py-3">
            <CheckCircle className="w-4 h-4 shrink-0" />
            {result.inserted} records imported successfully. No partial imports.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function FsmImport() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-graphite">FSM Data Import</h1>
        <p className="text-steel text-sm mt-1">
          Import technician assignments and consumption data via CSV. All rows must be valid — any error rejects the entire file.
        </p>
      </div>

      <div className="bg-surface rounded-xl border border-border px-4 py-3 text-sm text-steel space-y-1">
        <p className="font-semibold text-graphite flex items-center gap-2"><FileText className="w-4 h-4" />CSV Format Notes</p>
        <p>• <strong>technician_external_ref</strong>: Must match a known technician (e.g. ELV-001)</p>
        <p>• <strong>week_start_date</strong> / <strong>date</strong>: ISO format YYYY-MM-DD</p>
        <p>• <strong>planned_jobs_count</strong> / <strong>qty</strong>: Positive integers only</p>
        <p>• Download the template CSV for the exact column layout</p>
      </div>

      <ImportSection
        title="Assignments Import"
        description="Planned job counts per technician per week"
        endpoint="/api/admin/fsm-import/assignments"
        templateKey="assignments"
      />

      <ImportSection
        title="Consumption Import"
        description="Parts consumed per technician per order"
        endpoint="/api/admin/fsm-import/consumption"
        templateKey="consumption"
      />
    </div>
  );
}
